// App.js - MINIMAL CHANGE - Only adding password reset routing
import React, { useState, useEffect } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { TenantProvider, useTenant } from './contexts/TenantContext'
import AuthForm from './components/AuthForm'
import TimeTrackingApp from './components/TimeTrackingApp'
import LandingPage from './components/LandingPage'
import InvitationPage from './components/InvitationPage'
import BillingCompletionHandler from './components/BillingCompletionHandler'
import PasswordReset from './components/PasswordReset' // NEW: Import password reset component
import { Building2, Loader2, AlertCircle } from 'lucide-react'

// Main app content component
function AppContent() {
  const { user, loading: authLoading } = useAuth()
  const [currentView, setCurrentView] = useState('landing')
  const [authMode, setAuthMode] = useState('signin')
  const [invitationToken, setInvitationToken] = useState(null)
  const [loading, setLoading] = useState(true)

  // Check URL for invitation or current path
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const hashParams = new URLSearchParams(window.location.hash.substring(1))
    const inviteToken = urlParams.get('invite') || hashParams.get('invite')
    
    // NEW: Check for password reset
    const isPasswordReset = window.location.pathname === '/reset-password' || 
                           hashParams.get('type') === 'recovery'
    
    if (isPasswordReset) {
      setCurrentView('password-reset')
      setLoading(false)
      return
    }
    
    if (inviteToken) {
      setInvitationToken(inviteToken)
      setCurrentView('invitation')
      setLoading(false)
      return
    }

    const path = window.location.pathname
    
    if (authLoading) {
      return
    }

    setLoading(false)

    if (path === '/auth' || path.includes('auth')) {
      const isSignup = urlParams.get('signup') === 'true' || path.includes('signup')
      setAuthMode(isSignup ? 'signup' : 'signin')
      setCurrentView('auth')
    } else if (path === '/app') {
      setCurrentView(user ? 'app' : 'auth')
    } else {
      setCurrentView(user ? 'app' : 'landing')
    }
  }, [authLoading, user])

  // Handle browser navigation
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname
      const urlParams = new URLSearchParams(window.location.search)
      const hashParams = new URLSearchParams(window.location.hash.substring(1))
      
      // NEW: Check for password reset
      const isPasswordReset = path === '/reset-password' || 
                             hashParams.get('type') === 'recovery'
      
      if (isPasswordReset) {
        setCurrentView('password-reset')
        return
      }
      
      if (path === '/auth' || path.includes('auth')) {
        const isSignup = urlParams.get('signup') === 'true' || path.includes('signup')
        setAuthMode(isSignup ? 'signup' : 'signin')
        setCurrentView('auth')
      } else if (path === '/app') {
        setCurrentView(user ? 'app' : 'auth')
      } else {
        setCurrentView(user ? 'app' : 'landing')
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [user])

  // Navigation functions
  const navigateToHome = () => {
    window.history.pushState({}, '', '/')
  }

  const navigateToAuth = (isSignup = false) => {
    window.history.pushState({}, '', `/auth${isSignup ? '?signup=true' : ''}`)
  }

  const navigateToApp = () => {
    window.history.pushState({}, '', '/app')
  }

  // Navigation handlers
  const handleGetStarted = () => {
    setAuthMode('signup')
    setCurrentView('auth')
    navigateToAuth(true)
  }

  const handleSignIn = () => {
    setAuthMode('signin')
    setCurrentView('auth')
    navigateToAuth(false)
  }

  const handleAuthSuccess = () => {
    setCurrentView('app')
    navigateToApp()
  }

  const handleBackToLanding = () => {
    setCurrentView('landing')
    navigateToHome()
  }

  // NEW: Handle password reset success
  const handlePasswordResetSuccess = () => {
    setCurrentView('auth')
    setAuthMode('signin')
    navigateToAuth(false)
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 text-blue-600 animate-spin" />
          <div className="text-lg font-medium text-slate-800">Loading...</div>
        </div>
      </div>
    )
  }

  // Render based on current view
  switch (currentView) {
    // NEW: Password reset case
    case 'password-reset':
      return (
        <PasswordReset 
          onSuccess={handlePasswordResetSuccess}
        />
      )

    case 'invitation':
      return (
        <TenantProvider>
          <InvitationPage invitationToken={invitationToken} />
        </TenantProvider>
      )
    
    case 'auth':
      return (
        <AuthForm 
          initialMode={authMode}
          onSuccess={handleAuthSuccess}
          onBackToLanding={handleBackToLanding}
        />
      )
    
    case 'app':
      if (!user) {
        setCurrentView('auth')
        navigateToAuth()
        return null
      }
      return (
        <TenantProvider>
          <BillingCompletionHandler>
            <TenantSetup />
          </BillingCompletionHandler>
        </TenantProvider>
      )
    
    case 'landing':
    default:
      return (
        <LandingPage 
          onGetStarted={handleGetStarted}
          onSignIn={handleSignIn}
        />
      )
  }
}

// Enhanced TenantSetup component with better tenant detection
function TenantSetup() {
  const { user } = useAuth()
  const { tenants, loading, error, createTenantForUser, refetchTenants } = useTenant()
  const [creating, setCreating] = useState(false)
  const [tenantName, setTenantName] = useState('')
  const [setupError, setSetupError] = useState('')
  const [checkingForTenant, setCheckingForTenant] = useState(true)
  const [hasCheckedPendingTenant, setHasCheckedPendingTenant] = useState(false)

  // Check for pending tenant creation from signup
  useEffect(() => {
    if (user && !loading && !hasCheckedPendingTenant) {
      checkForPendingTenant()
    }
  }, [user, loading, hasCheckedPendingTenant])

  const checkForPendingTenant = async () => {
    try {
      setCheckingForTenant(true)
      setHasCheckedPendingTenant(true)

      // Check if user has tenant_name in metadata (from signup)
      const tenantName = user?.user_metadata?.tenant_name
      
      if (tenantName && tenants.length === 0) {
        console.log('Found tenant name in user metadata:', tenantName)
        console.log('No tenants loaded yet, checking if tenant creation is still pending...')
        
        // Wait a bit and refetch tenants to see if the trigger created one
        setTimeout(async () => {
          console.log('Refetching tenants to check for auto-created tenant...')
          await refetchTenants()
          
          // If still no tenants after refetch, the trigger might have failed
          // This is our fallback
          if (tenants.length === 0) {
            console.log('No tenants found after refetch, trigger may have failed. Creating tenant manually...')
            const result = await createTenantForUser(tenantName)
            if (!result.success) {
              setSetupError('Failed to create organization: ' + result.error)
            }
          }
        }, 2000)
      }
    } catch (error) {
      console.error('Error checking for pending tenant:', error)
      setSetupError('Error setting up your account')
    } finally {
      setCheckingForTenant(false)
    }
  }

  const handleCreateTenant = async () => {
    if (!tenantName.trim()) {
      setSetupError('Please enter an organization name')
      return
    }

    setCreating(true)
    setSetupError('')

    const result = await createTenantForUser(tenantName.trim())
    
    if (!result.success) {
      setSetupError(result.error)
    }
    
    setCreating(false)
  }

  if (loading || checkingForTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 text-blue-600 animate-spin" />
          <div className="text-lg font-medium text-slate-800">Setting up your account...</div>
          <div className="text-sm text-slate-600 mt-2">This may take a moment</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
          <div className="text-lg font-medium text-slate-800 mb-2">Setup Error</div>
          <div className="text-sm text-slate-600 mb-4">{error}</div>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (tenants.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800">Create Your Organization</h2>
            <p className="text-slate-600 mt-2">Let's set up your workspace to get started</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Organization Name
              </label>
              <input
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                placeholder="Enter your company or team name"
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCreateTenant()}
              />
            </div>

            {setupError && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                {setupError}
              </div>
            )}

            <button
              onClick={handleCreateTenant}
              disabled={creating || !tenantName.trim()}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {creating ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Creating...</span>
                </div>
              ) : (
                'Create Organization'
              )}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <TimeTrackingApp />
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App